import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Paper,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  Divider,

  Button,
  alpha
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import {
  Groups,
  FilterList,
  Call as CallIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  GroupAdd as GroupAddIcon,
  SaveAlt
} from "@mui/icons-material";
import * as XLSX from 'xlsx';
import { toast } from "react-toastify";
import { isArray, isEmpty } from "lodash";
import moment from "moment";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import useContacts from "../../hooks/useContacts";
import useMessages from "../../hooks/useMessages";
import { ChatsUser } from "./ChartsUser";
import ChartDonut from "./ChartDonut";
import Filters from "./Filters";
import { ChartsDate } from "./ChartsDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import Reports from "../Reports";
import Moments from "../Moments";
import { i18n } from "../../translate/i18n";

const Dashboard = () => {
  const themeV4 = useThemeV4();

  // Bridge V4 theme to V5 theme to ensure Whitelabel consistency
  const theme = React.useMemo(() => createTheme({
    palette: {
      mode: themeV4.palette.type,
      primary: themeV4.palette.primary,
      secondary: themeV4.palette.secondary,
      error: themeV4.palette.error,
      warning: themeV4.palette.warning,
      info: themeV4.palette.info,
      success: themeV4.palette.success,
      text: themeV4.palette.text,
      background: themeV4.palette.background,
      divider: themeV4.palette.divider,
    },
    shape: themeV4.shape,
    // Copy other needed properties if necessary
  }), [themeV4]);
  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [dateStartTicket, setDateStartTicket] = useState(moment().startOf('month').format("YYYY-MM-DD"));
  const [dateEndTicket, setDateEndTicket] = useState(moment().format("YYYY-MM-DD"));
  const [queueTicket, setQueueTicket] = useState(false);
  const [fetchDataFilter, setFetchDataFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const { find } = useDashboard();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    async function firstLoad() {
      await fetchData();
    }
    setTimeout(() => {
      firstLoad();
    }, 1000);
  }, [fetchDataFilter]);

  async function fetchData() {
    setLoading(true);
    let params = {};
    if (!isEmpty(dateStartTicket) && moment(dateStartTicket).isValid()) {
      params = { ...params, date_from: moment(dateStartTicket).format("YYYY-MM-DD") };
    }
    if (!isEmpty(dateEndTicket) && moment(dateEndTicket).isValid()) {
      params = { ...params, date_to: moment(dateEndTicket).format("YYYY-MM-DD") };
    }
    if (Object.keys(params).length === 0) {
      toast.error("Parametrize o filtro");
      setLoading(false);
      return;
    }
    const data = await find(params);
    setCounters(data.counters);
    if (isArray(data.attendants)) {
      setAttendants(data.attendants);
    } else {
      setAttendants([]);
    }
    setLoading(false);
  }

  const exportarGridParaExcel = () => {
    const ws = XLSX.utils.table_to_sheet(document.getElementById('grid-attendants'));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'RelatorioDeAtendentes');
    XLSX.writeFile(wb, 'relatorio-de-atendentes.xlsx');
  };

  const GetUsers = () => {
    let userOnline = 0;
    attendants.forEach(user => {
      if (user.online === true) {
        userOnline = userOnline + 1;
      }
    });
    return userOnline;
  };

  function toggleShowFilter() {
    setShowFilter(!showFilter);
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  const statCards = [
    {
      title: i18n.t("dashboard.cards.inAttendance"),
      value: counters.supportHappening || 0,
      icon: <CallIcon />,
      color: theme.palette.primary.main
    },
    {
      title: i18n.t("dashboard.cards.waiting"),
      value: counters.supportPending || 0,
      icon: <HourglassEmptyIcon />,
      color: theme.palette.warning.main
    },
    {
      title: i18n.t("dashboard.cards.finalized"),
      value: counters.supportFinished || 0,
      icon: <CheckCircleIcon />,
      color: theme.palette.success.main
    },
    {
      title: i18n.t("dashboard.cards.groups"),
      value: counters.supportGroups || 0,
      icon: <Groups />,
      color: "#8e44ad"
    },
    {
      title: i18n.t("dashboard.cards.activeAttendants"),
      value: `${GetUsers() || 0}/${attendants.length || 0}`,
      icon: <RecordVoiceOverIcon />,
      color: theme.palette.info.main
    },
    {
      title: i18n.t("dashboard.cards.newContacts"),
      value: counters.leads || 0,
      icon: <GroupAddIcon />,
      color: theme.palette.secondary.main
    }
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : theme.palette.background.default, 
        minHeight: "100%", 
        display: "flex",
        flexDirection: "column",
        flex: 1,
        py: 4, 
        px: 2 
      }}>
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
            <Box>
              <Typography variant="h4" fontWeight="800" color="primary" gutterBottom>
                Dashboard
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Visão geral e métricas de atendimento em tempo real
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FilterList />}
              onClick={toggleShowFilter}
              sx={{ borderRadius: 50, px: 3, textTransform: 'none', fontWeight: 'bold' }}
            >
              {showFilter ? "Ocultar Filtros" : "Mostrar Filtros"}
            </Button>
          </Box>

          {showFilter && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: "16px",
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)"
              }}
            >
              <Filters
                setDateStartTicket={setDateStartTicket}
                setDateEndTicket={setDateEndTicket}
                dateStartTicket={dateStartTicket}
                dateEndTicket={dateEndTicket}
                setQueueTicket={setQueueTicket}
                queueTicket={queueTicket}
                fetchData={setFetchDataFilter}
              />
            </Paper>
          )}

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {statCards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: "16px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                      borderColor: 'primary.light'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box>
                        <Typography variant="h4" fontWeight="800" color="textPrimary" sx={{ mb: 0.5 }}>
                          {card.value}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" fontWeight="600">
                          {card.title}
                        </Typography>
                      </Box>
                      <Avatar
                        sx={{
                          bgcolor: alpha(card.color, 0.2), // Opacidade 20%
                          color: card.color,
                          width: 42,
                          height: 42,
                          borderRadius: 3
                        }}
                      >
                        <SvgIcon>{card.icon}</SvgIcon>
                      </Avatar>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper
            elevation={0}
            sx={{
              border: 0,
              bgcolor: "transparent",
              mb: 4
            }}
          >
            <Box sx={{ borderBottom: 0, mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: "50px",
                  border: `1px solid ${theme.palette.divider}`,
                  p: 0.5,
                  minHeight: "56px",
                  "& .MuiTabs-flexContainer": {
                    gap: "8px",
                  },
                  "& .MuiTab-root": {
                    fontWeight: "600",
                    textTransform: "none",
                    minHeight: "48px",
                    px: 3,
                    borderRadius: "50px",
                    color: "text.secondary",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.text.primary, 0.05),
                      color: "primary.main",
                    },
                  },
                  "& .Mui-selected": {
                    color: "primary.main !important",
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >
                <Tab label={i18n.t("dashboard.tabs.performance")} />
                <Tab label={i18n.t("dashboard.tabs.assessments")} />
                <Tab label={i18n.t("dashboard.tabs.attendants")} />
                <Tab label="Relatórios" />
                <Tab label="Painel de Atendimento" />
              </Tabs>
            </Box>

            <Box sx={{ p: 3 }}>
              {activeTab === 0 && (
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom sx={{ mb: 3 }}>
                    {i18n.t("dashboard.charts.performance")}
                  </Typography>
                  <ChartsDate />
                </Box>
              )}

              {activeTab === 1 && (
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {i18n.t("dashboard.tabs.assessments")}
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      {/* Detalhamento NPS */}
                      <Grid item xs={12} md={4}>
                        <ChartDonut
                          title={i18n.t("dashboard.assessments.prosecutors")}
                          value={counters.npsPromotersPerc || 0}
                          data={[`{'name': 'Promotores', 'value': 100}`]}
                          color={[theme.palette.success.main]}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ChartDonut
                          title={i18n.t("dashboard.assessments.neutral")}
                          value={counters.npsPassivePerc || 0}
                          data={[`{'name': 'Neutros', 'value': 100}`]}
                          color={[theme.palette.warning.main]}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ChartDonut
                          title={i18n.t("dashboard.assessments.detractors")}
                          value={counters.npsDetractorsPerc || 0}
                          data={[`{'name': 'Detratores', 'value': 100}`]}
                          color={[theme.palette.error.main]}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ChartDonut
                      data={[
                        `{'name': 'Promotores', 'value': ${counters.npsPromotersPerc || 100}}`,
                        `{'name': 'Detratores', 'value': ${counters.npsDetractorsPerc || 0}}`,
                        `{'name': 'Neutros', 'value': ${counters.npsPassivePerc || 0}}`
                      ]}
                      value={counters.npsScore || 0}
                      title="NPS Score"
                      color={(parseInt(counters.npsPromotersPerc || 0) + parseInt(counters.npsDetractorsPerc || 0) + parseInt(counters.npsPassivePerc || 0)) === 0 ? ["#918F94"] : [theme.palette.success.main, theme.palette.error.main, theme.palette.warning.main]}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 3 }} />
                    <Grid container spacing={2} justifyContent="center">
                      <Grid item xs={12} md={4} textAlign="center">
                        <Typography variant="h3" fontWeight="bold" color="primary">{counters.tickets || 0}</Typography>
                        <Typography variant="body2" color="textSecondary">{i18n.t("dashboard.assessments.totalCalls")}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4} textAlign="center">
                        <Typography variant="h3" fontWeight="bold" color="primary">{counters.withRating || 0}</Typography>
                        <Typography variant="body2" color="textSecondary">{i18n.t("dashboard.assessments.ratedCalls")}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4} textAlign="center">
                        <Typography variant="h3" fontWeight="bold" color="primary">
                          {Number(counters.percRating / 100 || 0).toLocaleString(undefined, { style: 'percent' })}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">{i18n.t("dashboard.assessments.evaluationIndex")}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {activeTab === 2 && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {i18n.t("dashboard.tabs.attendants")}
                    </Typography>
                    <Button
                      startIcon={<SaveAlt />}
                      onClick={exportarGridParaExcel}
                      variant="outlined"
                      color="primary"
                      sx={{ borderRadius: 50, textTransform: 'none' }}
                    >
                      Exportar Excel
                    </Button>
                  </Box>

                  <div id="grid-attendants">
                    {attendants.length > 0 && (
                      <TableAttendantsStatus attendants={attendants} loading={loading} />
                    )}
                  </div>

                  <Divider sx={{ my: 4 }} />

                  <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                    {i18n.t("dashboard.charts.userPerformance")}
                  </Typography>
                  <ChatsUser />
                </Box>
              )}

              {activeTab === 3 && (
                <Box>
                  <Reports isEmbedded />
                </Box>
              )}

              {activeTab === 4 && (
                <Box>
                  <Moments isEmbedded />
                </Box>
              )}
            </Box>
          </Paper>

        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Dashboard;